import { useState, useEffect, useCallback } from 'react';
import { Badge, Button, Modal } from '@safetyhub/ui';
import {
  getReportStakeholders,
  shareReport,
  StakeholdersResponse,
  ShareRecipient,
  ShareReportDto,
} from '../../lib/reports';
import { extractApiError } from '../../lib/api';
import { formatDateToLocal } from '@safetyhub/shared';
import { MODAL_LAYER } from '../modalLayers';

interface ShareReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  reportTitle: string;
  companyId: string;
  /** 표시 라벨 (기본값: '보고서') */
  label?: string;
}

interface DirectRecipient {
  id: string;
  contact: string; // 이메일 또는 전화번호
  contactType: 'email' | 'phone';
}

// 관계자 채널 선택 상태: { "author:id": { email: true, phone: false } }
interface ChannelSelection {
  email: boolean;
  phone: boolean;
}

const EXPIRY_OPTIONS = [
  { value: 7, label: '7일' },
  { value: 14, label: '14일' },
  { value: 30, label: '30일' },
  { value: 60, label: '60일' },
  { value: 90, label: '90일' },
];

// 이메일 형식 체크
function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

// 전화번호 형식 체크 (숫자, -, 공백만 허용, 최소 10자리)
function isPhone(value: string): boolean {
  const digits = value.replace(/[\s-]/g, '');
  return /^[0-9]{10,}$/.test(digits);
}

// 입력값이 이메일인지 전화번호인지 판단
function detectContactType(value: string): 'email' | 'phone' | null {
  if (isEmail(value)) return 'email';
  if (isPhone(value)) return 'phone';
  return null;
}

export function ShareReportModal({
  isOpen,
  onClose,
  reportId,
  reportTitle,
  companyId,
  label: displayLabel = '보고서',
}: ShareReportModalProps) {
  // 관계자 데이터
  const [stakeholders, setStakeholders] = useState<StakeholdersResponse | null>(null);
  const [isLoadingStakeholders, setIsLoadingStakeholders] = useState(false);

  // 선택된 관계자 채널: Map<"type:id", {email: boolean, phone: boolean}>
  const [selectedChannels, setSelectedChannels] = useState<Map<string, ChannelSelection>>(
    new Map()
  );

  // 직접 입력 수신자
  const [directRecipients, setDirectRecipients] = useState<DirectRecipient[]>([]);
  const [newContact, setNewContact] = useState('');
  const [contactError, setContactError] = useState<string | null>(null);

  // 발송 옵션
  const [expiresInDays, setExpiresInDays] = useState(30);

  // 발송 상태
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    sharedCount: number;
    shareUrl: string;
    expiresAt: string;
  } | null>(null);

  // 관계자 데이터 로드
  const loadStakeholders = useCallback(async () => {
    setIsLoadingStakeholders(true);
    setError(null);
    try {
      const data = await getReportStakeholders(reportId, companyId);
      setStakeholders(data);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoadingStakeholders(false);
    }
  }, [reportId, companyId]);

  useEffect(() => {
    if (isOpen) {
      loadStakeholders();
      // 초기화
      setSelectedChannels(new Map());
      setDirectRecipients([]);
      setNewContact('');
      setContactError(null);
      setExpiresInDays(30);
      setResult(null);
      setError(null);
    }
  }, [isOpen, loadStakeholders]);

  // 관계자 채널 토글
  const toggleChannel = (type: string, id: string, channel: 'email' | 'phone') => {
    const key = `${type}:${id}`;
    setSelectedChannels((prev) => {
      const next = new Map(prev);
      const current = next.get(key) || { email: false, phone: false };
      next.set(key, {
        ...current,
        [channel]: !current[channel],
      });
      return next;
    });
  };

  // 관계자의 특정 채널이 선택되었는지 확인
  const isChannelSelected = (type: string, id: string, channel: 'email' | 'phone'): boolean => {
    const key = `${type}:${id}`;
    const selection = selectedChannels.get(key);
    return selection?.[channel] ?? false;
  };

  // 직접 입력 수신자 추가
  const addDirectRecipient = () => {
    const trimmed = newContact.trim();
    if (!trimmed) {
      setContactError('이메일 또는 전화번호를 입력해주세요.');
      return;
    }

    const contactType = detectContactType(trimmed);
    if (!contactType) {
      setContactError('올바른 이메일 또는 전화번호 형식이 아닙니다.');
      return;
    }

    // 중복 체크
    const exists = directRecipients.some((r) => r.contact === trimmed);
    if (exists) {
      setContactError('이미 추가된 수신자입니다.');
      return;
    }

    setDirectRecipients((prev) => [
      ...prev,
      {
        id: `direct-${Date.now()}`,
        contact: trimmed,
        contactType,
      },
    ]);
    setNewContact('');
    setContactError(null);
  };

  // Enter 키로 추가
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDirectRecipient();
    }
  };

  // 직접 입력 수신자 제거
  const removeDirectRecipient = (id: string) => {
    setDirectRecipients((prev) => prev.filter((r) => r.id !== id));
  };

  // 만료일 계산
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + expiresInDays);
  const expiryDateStr = formatDateToLocal(expiryDate);

  // 선택된 수신자 수 계산
  const getSelectedCount = (): number => {
    let count = directRecipients.length;

    // 관계자 중 최소 하나의 채널이 선택된 경우 카운트
    selectedChannels.forEach((channels) => {
      if (channels.email || channels.phone) {
        count++;
      }
    });

    return count;
  };

  // 공유 실행
  const handleShare = async () => {
    if (isSharing) return;

    // 수신자 목록 구성
    const recipients: ShareRecipient[] = [];
    const sendViaSet = new Set<'email' | 'sms'>();

    // 관계자 추가 (선택된 채널 정보를 direct 타입으로 변환)
    if (stakeholders) {
      // 작성자
      const authorKey = `author:${stakeholders.author.id}`;
      const authorChannels = selectedChannels.get(authorKey);
      if (authorChannels?.email || authorChannels?.phone) {
        recipients.push({
          type: 'direct',
          name: stakeholders.author.name,
          email: authorChannels.email ? stakeholders.author.email : undefined,
          phone: authorChannels.phone ? stakeholders.author.phone : undefined,
        });
        if (authorChannels.email) sendViaSet.add('email');
        if (authorChannels.phone) sendViaSet.add('sms');
      }

      // 기여자
      stakeholders.contributors.forEach((contributor) => {
        const key = `contributor:${contributor.id}`;
        const channels = selectedChannels.get(key);
        if (channels?.email || channels?.phone) {
          recipients.push({
            type: 'direct',
            name: contributor.name,
            email: channels.email ? contributor.email : undefined,
            phone: channels.phone ? contributor.phone : undefined,
          });
          if (channels.email) sendViaSet.add('email');
          if (channels.phone) sendViaSet.add('sms');
        }
      });

      // 결재자
      stakeholders.approvers.forEach((approver) => {
        const key = `approver:${approver.id}`;
        const channels = selectedChannels.get(key);
        if (channels?.email || channels?.phone) {
          recipients.push({
            type: 'direct',
            name: approver.name,
            email: channels.email ? approver.email : undefined,
            phone: channels.phone ? approver.phone : undefined,
          });
          if (channels.email) sendViaSet.add('email');
          if (channels.phone) sendViaSet.add('sms');
        }
      });
    }

    // 직접 입력 추가
    directRecipients.forEach((r) => {
      if (r.contactType === 'email') {
        recipients.push({
          type: 'direct',
          name: r.contact, // 이메일 자체를 이름으로 사용
          email: r.contact,
        });
        sendViaSet.add('email');
      } else {
        recipients.push({
          type: 'direct',
          name: r.contact, // 전화번호 자체를 이름으로 사용
          phone: r.contact,
        });
        sendViaSet.add('sms');
      }
    });

    if (recipients.length === 0) {
      setError('최소 1명의 수신자를 선택하거나 입력해주세요.');
      return;
    }

    const sendVia = Array.from(sendViaSet);

    setIsSharing(true);
    setError(null);

    try {
      const dto: ShareReportDto = {
        recipients,
        sendVia,
        expiresInDays,
      };

      const response = await shareReport(reportId, companyId, dto);
      setResult({
        sharedCount: response.sharedCount,
        shareUrl: response.shareUrl,
        expiresAt: response.expiresAt,
      });
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsSharing(false);
    }
  };

  if (!isOpen) return null;

  // 관계자 채널 체크박스 렌더링
  const renderStakeholderChannels = (
    key: string,
    type: string,
    id: string,
    name: string,
    role: string,
    email?: string,
    phone?: string
  ) => {
    const hasEmail = !!email;
    const hasPhone = !!phone;

    if (!hasEmail && !hasPhone) {
      return (
        <div key={key} className="p-3 text-sm text-ink-400">
          <span className="font-medium text-ink-600">{name}</span>
          <span className="ml-2 text-xs">({role})</span>
          <span className="ml-2">- 연락처 없음</span>
        </div>
      );
    }

    return (
      <div key={key} className="p-3">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-ink-900">{name}</span>
            <span className="ml-2 text-xs text-ink-500">({role})</span>
          </div>
          <div className="flex items-center gap-4">
            {hasEmail && (
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChannelSelected(type, id, 'email')}
                  onChange={() => toggleChannel(type, id, 'email')}
                  className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="ml-1.5 text-xs text-ink-600">이메일</span>
              </label>
            )}
            {hasPhone && (
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChannelSelected(type, id, 'phone')}
                  onChange={() => toggleChannel(type, id, 'phone')}
                  className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="ml-1.5 text-xs text-ink-600">전화번호</span>
              </label>
            )}
          </div>
        </div>
        <div className="mt-1 text-xs text-ink-400">
          {hasEmail && <span className="mr-3">{email}</span>}
          {hasPhone && <span>{phone}</span>}
        </div>
      </div>
    );
  };

  // 공유(발송) 중에는 닫기 수단을 전부 막는다 — 종전엔 오버레이 클릭으로 발송 도중 닫혔다.
  // 결과 화면은 아이콘 헤딩이 제목을 소유하므로 base 헤더(제목·✕)를 쓰지 않고 접근 이름만 준다.
  return (
    <Modal
      open
      onClose={onClose}
      title={result ? undefined : `${displayLabel} 공유`}
      ariaLabel={result ? '공유 완료' : undefined}
      size="lg"
      overlayZClassName={MODAL_LAYER.root}
      showCloseButton={!result && !isSharing}
      closeOnOverlay={!isSharing}
      closeOnEsc={!isSharing}
      closeLocked={isSharing}
      footer={
        result ? (
          <Button variant="primary" className="w-full" onClick={onClose}>
            닫기
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={isSharing}>
              취소
            </Button>
            <Button
              variant="primary"
              disabled={isSharing || getSelectedCount() === 0}
              onClick={handleShare}
            >
              {isSharing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  공유 중...
                </>
              ) : (
                `공유하기${getSelectedCount() > 0 ? ` (${getSelectedCount()}명)` : ''}`
              )}
            </Button>
          </>
        )
      }
    >
      {/* 결과 화면 */}
      {result ? (
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckIcon className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-ink-900">공유 완료</h3>
          <p className="mt-2 text-sm text-ink-500">
            {result.sharedCount}명에게 {displayLabel}를 공유했습니다.
          </p>
          <div className="mt-4 p-3 bg-surface-soft rounded-card">
            <p className="text-xs text-ink-500 mb-1">공유 링크</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={result.shareUrl}
                className="flex-1 text-sm text-ink-700 bg-white border border-line rounded-control px-2 py-1"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigator.clipboard.writeText(result.shareUrl)}
              >
                복사
              </Button>
            </div>
            <p className="mt-2 text-xs text-ink-500">
              유효 기간: {formatDateToLocal(new Date(result.expiresAt))}까지
            </p>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-sm text-ink-500 mb-4">
            "{reportTitle}" {displayLabel}를 공유합니다.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-600">
              {error}
            </div>
          )}

          {/* 관계자 선택 */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-ink-700 mb-2">관계자</h4>
            {isLoadingStakeholders ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600" />
              </div>
            ) : stakeholders ? (
              <div className="border border-line rounded-card divide-y divide-line">
                {/* 작성자 */}
                {renderStakeholderChannels(
                  `author-${stakeholders.author.id}`,
                  'author',
                  stakeholders.author.id,
                  stakeholders.author.name,
                  '작성자',
                  stakeholders.author.email,
                  stakeholders.author.phone
                )}

                {/* 기여자 */}
                {stakeholders.contributors.map((contributor) =>
                  renderStakeholderChannels(
                    `contributor-${contributor.id}`,
                    'contributor',
                    contributor.id,
                    contributor.name,
                    '기여자',
                    contributor.email,
                    contributor.phone
                  )
                )}

                {/* 결재자 */}
                {stakeholders.approvers.map((approver) =>
                  renderStakeholderChannels(
                    `approver-${approver.id}`,
                    'approver',
                    approver.id,
                    approver.name,
                    '결재자',
                    approver.email,
                    approver.phone
                  )
                )}

                {stakeholders.contributors.length === 0 &&
                  stakeholders.approvers.length === 0 &&
                  !stakeholders.author.email &&
                  !stakeholders.author.phone && (
                    <div className="p-3 text-sm text-ink-500">
                      연락처가 등록된 관계자가 없습니다.
                    </div>
                  )}
              </div>
            ) : null}
          </div>

          {/* 직접 입력 */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-ink-700 mb-2">직접 입력</h4>

            {/* 추가된 수신자 목록 */}
            {directRecipients.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {directRecipients.map((r) => (
                  <div
                    key={r.id}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-surface-hover rounded-full text-sm"
                  >
                    <Badge
                      shape="rounded"
                      size="sm"
                      tone={r.contactType === 'email' ? 'brand' : 'emerald'}
                    >
                      {r.contactType === 'email' ? '이메일' : 'SMS'}
                    </Badge>
                    <span className="text-ink-700">{r.contact}</span>
                    <button
                      onClick={() => removeDirectRecipient(r.id)}
                      className="text-ink-400 hover:text-red-500 ml-1"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 입력 폼 */}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="이메일 또는 전화번호 입력"
                value={newContact}
                onChange={(e) => {
                  setNewContact(e.target.value);
                  setContactError(null);
                }}
                onKeyDown={handleKeyDown}
                className={`flex-1 border rounded-control px-3 py-2 text-sm ${
                  contactError ? 'border-red-300' : 'border-line'
                }`}
              />
              <Button variant="secondary" onClick={addDirectRecipient}>
                추가
              </Button>
            </div>
            {contactError && (
              <p className="mt-1 text-xs text-red-500">{contactError}</p>
            )}
            <p className="mt-1 text-xs text-ink-400">
              이메일 형식(예: user@email.com) 또는 전화번호(숫자만)를 입력하면 자동으로 구분됩니다.
            </p>
          </div>

          {/* 유효 기간 */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-ink-700 mb-2">유효 기간</h4>
            <div className="flex items-center gap-2">
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                className="border border-line rounded-control px-3 py-2 text-sm"
              >
                {EXPIRY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="text-sm text-ink-500">
                (만료일: {expiryDateStr})
              </span>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// 아이콘 컴포넌트
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
